import { Button, Icon } from '@pwc/pwc-deals-ui';
import classNames from 'classnames';
import PropType from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isString } from 'lodash';
import { isPowerPivotLibraryItem } from 'features/analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes.ts';
import {
  getReportFormatLayoutState,
  setActiveSectionId,
  setFloatingTabsHeight,
  setFloatingTabsTop,
} from '../../../../../../../../common/redux/reportFormat/reportFormatSlice';
import { useSections } from '../../../../../FreeformAnalysisForm/FreeformAnalysisFormContext';
import { setSectionToScroll } from '../../../../../FreeformAnalysisForm/FreeformAnalysisFormContextUtils';
import CONSTANTS from '../../../Constants';
import LayoutIcon from '../../../LayoutIcon';
import SectionControls from '../../../SectionControls';

import './Section.scss';
import SectionArea from './SectionArea/SectionArea';
import useMobile from '../../../../../../../../common/hooks/useMobile';

function Section({
  controlsActive,
  droppableLocations,
  hideExcelCellRangeFormatSwitch,
  lastSection,
  libraryItemMap,
  onAddSection,
  onMoveSection,
  onRemoveSection,
  scrollIntoView = true,
  sectionData,
  showRightPanel,
  targetDroppableLocationIds,
}) {
  const ref = useRef();
  const [isActive, setIsActive] = useState(false);
  const [{ sectionIdToScroll }, useSectionsDispatch] = useSections();
  const isMobile = useMobile();

  const dispatch = useDispatch();
  const sectionId = sectionData.order - 1;

  const { activeSectionIndex, floatingTabsHeight } = useSelector(
    getReportFormatLayoutState
  );

  const currentHeight = ref.current ? ref.current.offsetHeight : 0;
  const currentTop = ref.current ? ref.current.offsetTop : 0;
  const { t } = useTranslation();

  useEffect(() => {
    if (sectionIdToScroll === sectionId && currentHeight) {
      if (scrollIntoView) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
        });
      }
      setSectionToScroll(null, useSectionsDispatch);
    }
  }, [
    currentHeight,
    useSectionsDispatch,
    scrollIntoView,
    sectionId,
    sectionIdToScroll,
  ]);

  const handleOnSectionClick = () => {
    /*
     * Bug: PWC-5795
     * If section clicked, floatingTabs needs to be positioned to that section
     */
    if (!isMobile && activeSectionIndex !== sectionId) {
      dispatch(setActiveSectionId({ index: sectionId }));
      dispatch(
        setFloatingTabsTop({
          top: currentTop,
        })
      );
      if (currentHeight !== floatingTabsHeight) {
        dispatch(
          setFloatingTabsHeight({
            height: currentHeight,
          })
        );
      }
    }
  };

  useEffect(() => {
    if (activeSectionIndex === sectionId) {
      if (!isActive) {
        dispatch(
          setFloatingTabsTop({
            top: currentTop,
          })
        );
        dispatch(
          setFloatingTabsHeight({
            height: currentHeight,
          })
        );
      }

      setIsActive(true);
    } else if (isActive) {
      setIsActive(false);
    }
  }, [
    isActive,
    activeSectionIndex,
    sectionId,
    dispatch,
    currentTop,
    currentHeight,
  ]);

  const handleRemoveSection = () => {
    const newSectionIndex = Math.max(0, sectionData.order - 1 - 1);
    dispatch(setActiveSectionId({ index: newSectionIndex }));

    onRemoveSection(sectionData.order);
  };

  const handleMoveSection = (direction) => {
    onMoveSection(direction, sectionData);
  };

  return (
    // eslint-disable-next-line
    <div
      ref={ref}
      data-section-id={sectionId}
      key={`Section - ${sectionData.order}`}
      className="section"
      data-testid="section"
      onClick={handleOnSectionClick}
    >
      <div className="section__top-row" data-testid="section-top-row">
        <div className="section__top-row__row-1_2" />
        <div className="section__top-row__row-2_2">
          <SectionControls
            section={sectionData}
            lastSection={lastSection}
            controlsActive={controlsActive}
            handleMoveSection={handleMoveSection}
            handleRemoveSection={handleRemoveSection}
          />
        </div>
      </div>
      <div
        className={classNames('section__middle-row', {
          [CONSTANTS.LAYOUT_TYPE_TO_SECTION_CLASSNAME_MAP[
            sectionData.layoutType
          ]]: sectionData.layoutType,
        })}
        data-testid="section-middle-row"
      >
        {targetDroppableLocationIds &&
          targetDroppableLocationIds.map((droppableLocationId, panelNumber) => {
            const droppableLocation = droppableLocations[droppableLocationId];
            const libraryItems =
              droppableLocation &&
              droppableLocation.libraryItemIds
                .map((id) => libraryItemMap[id])
                .filter((libraryItem) => !!libraryItem);
            if (libraryItems) {
              // TODO: Remove logic inside this condition once BE adjusts API to return properties, from data object, in camel case
              libraryItems.forEach((item) => {
                if (isPowerPivotLibraryItem(item.type) && item.data) {
                  if (isString(item.data)) {
                    item.data = JSON.parse(item.data);
                  }
                  item.data = {
                    worksheetName:
                      item.data.worksheetName || item.data.WorksheetName,
                    name: item.data.name || item.data.Name,
                    uniqueId: item.data.uniqueId || item.data.UniqueId,
                    sharePointItemUrl:
                      item.data.sharePointItemUrl ||
                      item.data.SharePointItemUrl,
                    embeddedUrl: item.data.embeddedUrl || item.data.EmbeddedUrl,
                    errorCode: item.data.errorCode || item.data.ErrorCode,
                    siteId: item.data.siteId || item.data.SiteId,
                    driveId: item.data.driveId || item.data.DriveId,
                    driveItemId: item.data.driveItemId || item.data.DriveItemId,
                  };
                }
              });
            }

            return (
              <React.Fragment key={droppableLocationId}>
                {droppableLocation && (
                  <>
                    {isMobile && (
                      <LayoutIcon
                        name={
                          CONSTANTS.LAYOUT_TYPE_TO_ICON_MAP[
                            `${sectionData.layoutType}-${droppableLocation.position}`
                          ]
                        }
                      />
                    )}
                    <SectionArea
                      areaClass={droppableLocationId}
                      droppableLocation={
                        droppableLocations[droppableLocationId]
                      }
                      droppableLocations={droppableLocations}
                      hideExcelCellRangeFormatSwitch={
                        hideExcelCellRangeFormatSwitch
                      }
                      key={droppableLocationId}
                      libraryItemMap={libraryItemMap}
                      libraryItems={libraryItems}
                      panelNumber={panelNumber}
                      sectionData={sectionData}
                      showRightPanel={showRightPanel}
                    />
                  </>
                )}
              </React.Fragment>
            );
          })}
      </div>
      {lastSection && (
        <div className="section__bottom-row" data-testid="section-bottom-row">
          <hr className="section-line" />
          <Button className="button-no-underline" onClick={onAddSection}>
            <Icon
              name="add"
              width={24}
              height={24}
              className="fill icon-orange-theme"
            />
            <span className="section__bottom-row__button-text">
              {t('addNewSection')}
            </span>
          </Button>
          <hr className="section-line" />
        </div>
      )}
      <hr
        className="add-margin-inedit"
        style={{
          display: !lastSection ? 'block' : 'none',
          backgroundColor:
            localStorage.getItem('pwc-theme') === 'theme-dark'
              ? '#464646'
              : '#BBBBBB',
        }}
      />
    </div>
  );
}

SectionArea.propTypes = {
  sectionData: PropType.shape({
    order: PropType.number,
    layoutType: PropType.number,
    items: PropType.arrayOf(PropType.any),
  }),
  onAddSection: PropType.func,
  onRemoveSection: PropType.func,
  targetDroppableLocationIds: PropType.arrayOf(PropType.string),
  lastSection: PropType.bool,
  activeSection: PropType.bool,
};

export default Section;
